import React, { useState, useEffect, useRef } from "react"
import { Button } from "../ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog"
import { Label } from "../ui/label"
import { Textarea } from "../ui/textarea"
import { MessageSquare, Send, Check } from "lucide-react"
import { smsApi, SMS_SENT_LEAD_ARCHIVED, SMS_TRIGGER_MANUAL, type SmsTemplateOption } from "../../services/smsApi"
import { attorneysApi } from "../../services/attorneysApi"
import { useMyContext } from "../../context/Context"
import { shouldShowAttorneyPicker } from "./smsTemplateRules"

interface AttorneyOption {
  id: string
  name: string
  phone: string
  email: string
}

interface SmsTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  lead: {
    id: string
    name: string
    first_name: string
    phone: string
    assigned_to_attorney?: {
      name: string
      phone: string
      email: string
    } | null
  }
  onSmsSent: (
    template: string,
    status: "success" | "error",
    message: string,
    meta?: { archived?: boolean },
  ) => void
}

const DEFAULT_TEMPLATE_KEY = "UNANSWERED"

export default function SmsTemplateModal({
  isOpen,
  onClose,
  lead,
  onSmsSent
}: Readonly<SmsTemplateModalProps>) {
  const { user } = useMyContext()
  const [attorneys, setAttorneys] = useState<AttorneyOption[]>([])
  const [templates, setTemplates] = useState<SmsTemplateOption[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedAttorneyId, setSelectedAttorneyId] = useState("")
  const [selectedTemplate, setSelectedTemplate] = useState(DEFAULT_TEMPLATE_KEY)
  const [sending, setSending] = useState(false)
  const [rendering, setRendering] = useState(false)
  const [previewMessage, setPreviewMessage] = useState("")
  const previewTouchedRef = useRef(false)
  const renderRequestId = useRef(0)
  const [loadError, setLoadError] = useState("")

  const selectedDef = templates.find((template) => template.key === selectedTemplate)
  const needsAttorney = shouldShowAttorneyPicker(selectedDef)

  useEffect(() => {
    if (!isOpen) return

    const load = async () => {
      setLoading(true)
      setLoadError("")
      try {
        const [templateResponse, attorneyResponse] = await Promise.all([
          smsApi.getTemplates(),
          attorneysApi.getAttorneys(1, 10),
        ])
        const templateList = templateResponse.templates || []
        setTemplates(templateList)
        const defaultKey = templateList.some((template) => template.key === DEFAULT_TEMPLATE_KEY)
          ? DEFAULT_TEMPLATE_KEY
          : templateList[0]?.key || ""
        setSelectedTemplate(defaultKey)

        const attorneysList: AttorneyOption[] = attorneyResponse.attorneys
          .filter((attorney) => attorney.can_send_sms && attorney.phone && attorney.email)
          .map((attorney) => ({
            id: attorney.id,
            name: attorney.name,
            phone: attorney.phone,
            email: attorney.email,
          }))
        setAttorneys(attorneysList)
        if (attorneysList.length > 0) {
          const matching = user?.email
            ? attorneysList.find((attorney) => attorney.email.toLowerCase() === user.email.toLowerCase())
            : undefined
          setSelectedAttorneyId((matching || attorneysList[0]).id)
        }
      } catch (error) {
        console.error("Failed to load SMS templates:", error)
        const detail = error instanceof Error ? error.message : "Could not load SMS templates."
        setLoadError(detail)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [isOpen, user?.email])

  useEffect(() => {
    if (!isOpen || previewTouchedRef.current || !selectedDef) return
    if (needsAttorney && !selectedAttorneyId) return

    const requestId = renderRequestId.current + 1
    renderRequestId.current = requestId
    const render = async () => {
      setRendering(true)
      try {
        const result = await smsApi.renderTemplate({
          lead_id: lead.id,
          template_key: selectedDef.key,
          attorney_id: needsAttorney ? selectedAttorneyId : undefined,
        })
        if (renderRequestId.current === requestId && !previewTouchedRef.current) {
          setPreviewMessage(result.body)
          setLoadError("")
        }
      } catch (error) {
        console.error("Failed to render SMS template:", error)
        if (renderRequestId.current === requestId) setLoadError("Could not prepare this message.")
      } finally {
        if (renderRequestId.current === requestId) setRendering(false)
      }
    }

    render()
  }, [isOpen, selectedDef, needsAttorney, selectedAttorneyId, lead.id])

  const markPreviewUntouched = () => {
    previewTouchedRef.current = false
  }

  const handleTemplateSelect = (key: string) => {
    setSelectedTemplate(key)
    markPreviewUntouched()
  }

  const handleSendSms = async () => {
    if (!lead.phone) {
      onSmsSent(selectedTemplate, "error", "No phone number available for this lead")
      return
    }
    if (needsAttorney && !selectedAttorneyId) {
      onSmsSent(selectedTemplate, "error", "Please select an attorney")
      return
    }

    setSending(true)
    try {
      const result = await smsApi.sendSms({
        lead_id: lead.id,
        template_key: selectedTemplate,
        message_body: previewMessage,
        attorney_id: needsAttorney ? selectedAttorneyId : undefined,
        trigger: SMS_TRIGGER_MANUAL,
      })

      if (result.error === false) {
        if (result.archived) {
          onSmsSent(selectedTemplate, "success", SMS_SENT_LEAD_ARCHIVED, { archived: true })
        } else {
          onSmsSent(selectedTemplate, "success", `SMS sent successfully to ${lead.name}`)
        }
        onClose()
      } else {
        onSmsSent(selectedTemplate, "error", result.message || "Failed to send SMS")
      }
    } catch (error) {
      console.error("Failed to send SMS:", error)
      const message = error instanceof Error ? error.message : "Failed to send SMS. Please try again."
      onSmsSent(selectedTemplate, "error", message)
    } finally {
      setSending(false)
    }
  }

  const handleClose = () => {
    setSelectedAttorneyId("")
    setSelectedTemplate(DEFAULT_TEMPLATE_KEY)
    setPreviewMessage("")
    markPreviewUntouched()
    setSending(false)
    setLoadError("")
    onClose()
  }

  if (!isOpen) return null

  const sendDisabled =
    sending ||
    rendering ||
    loading ||
    !lead.phone ||
    !previewMessage.trim() ||
    (needsAttorney && (attorneys.length === 0 || !selectedAttorneyId))

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="mx-auto w-[95vw] max-w-2xl transition-none p-4 sm:p-5 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Send SMS to {lead.name}
          </DialogTitle>
          <DialogDescription>
            Choose a template and edit the message before sending
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {loadError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {loadError}
            </div>
          )}

          {needsAttorney && (
            <div className="space-y-1">
              <Label className="text-sm font-medium text-slate-700">Select Attorney</Label>
              {loading ? (
                <div className="p-2 text-sm text-slate-500">Loading attorneys...</div>
              ) : attorneys.length === 0 ? (
                <div className="p-2 text-sm text-red-500">No attorneys available</div>
              ) : (
                <div className="space-y-1 max-h-28 overflow-y-auto">
                  {attorneys.map((attorney) => (
                    <div
                      key={attorney.id}
                      className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                        selectedAttorneyId === attorney.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      onClick={() => {
                        setSelectedAttorneyId(attorney.id)
                        markPreviewUntouched()
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{attorney.name}</span>
                        {selectedAttorneyId === attorney.id && (
                          <Check className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-sm font-medium text-slate-700">Select Template</Label>
            {loading ? (
              <div className="p-2 text-sm text-slate-500">Loading templates...</div>
            ) : (
              <div className="space-y-1">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className={`cursor-pointer rounded-lg border px-2.5 py-1.5 transition-colors ${
                      selectedTemplate === template.key
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    onClick={() => handleTemplateSelect(template.key)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-36 shrink-0 text-sm font-medium text-slate-900 sm:w-40">
                        {template.label}
                      </span>
                      <span className="min-w-0 flex-1 text-xs text-slate-600 sm:text-right">
                        {template.description}
                      </span>
                      {selectedTemplate === template.key ? (
                        <Check className="h-4 w-4 shrink-0 text-blue-600" />
                      ) : (
                        <span className="h-4 w-4 shrink-0" aria-hidden />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="sms-message" className="text-sm font-medium text-slate-700">Message</Label>
            <Textarea
              id="sms-message"
              value={previewMessage}
              onChange={(event) => {
                previewTouchedRef.current = true
                setPreviewMessage(event.target.value)
              }}
              rows={6}
              className="text-sm"
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2 pt-2">
          <Button variant="outline" onClick={handleClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSendSms}
            disabled={sendDisabled}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {sending ? "Sending..." : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send SMS
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
